import React, { useState, useRef } from 'react';
import './DebugMenu.css';

// Mini SVG preview of a geometry shape
const ShapePreview = React.memo(({ divisions, path, size = 36 }) => {
  const center = size / 2;
  const radius = size / 2 - 2;

  const points = Array.from({ length: divisions }, (_, i) => {
    const angle = ((i * 360) / divisions - 90) * (Math.PI / 180);
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle),
    };
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shape-preview">
      {path.map(({ from, to }, i) => (
        <line
          key={i}
          x1={points[from].x}
          y1={points[from].y}
          x2={points[to].x}
          y2={points[to].y}
          stroke="white"
          strokeWidth="1"
        />
      ))}
    </svg>
  );
});

const DebugMenu = ({
  geometryList,
  disabledGeos,
  onToggle,
  onEnableAll,
  onDisableAll,
  liveMode,
  onToggleLive,
  noteLookback,
  onNoteLookbackChange,
  circles,
  disabledCircles,
  onToggleCircle,
  showTraditionalTime,
  onToggleTraditionalTime,
  showTimeline,
  onToggleTimeline,
  trailMode,
  onToggleTrailMode,
  onImportNotes,
  onExportNotes,
}) => {
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importStatus, setImportStatus] = useState(null);
  const fileInputRef = useRef(null);

  const runImport = (jsonString) => {
    try {
      const result = onImportNotes(jsonString);
      setImportStatus({
        ok: true,
        message: `Imported ${result.imported} entr${result.imported === 1 ? 'y' : 'ies'}${result.skipped ? ` (${result.skipped} duplicate/invalid skipped)` : ''}`,
      });
      setImportText('');
    } catch (err) {
      setImportStatus({ ok: false, message: `Import failed: ${err.message}` });
    }
  };

  const handleFileChosen = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => runImport(String(reader.result));
    reader.onerror = () => setImportStatus({ ok: false, message: 'Could not read file' });
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div className="debug-menu">
      <div className="debug-menu__content">
        <div className="debug-menu__header">
          <label className="debug-menu__live-toggle">
            <input
              type="checkbox"
              checked={liveMode}
              onChange={(e) => onToggleLive(e.target.checked)}
            />
            <span className="debug-menu__live-label">LIVE</span>
          </label>
          <button
            className={`debug-menu__action ${showTimeline ? 'debug-menu__action--active' : ''}`}
            onClick={onToggleTimeline}
          >
            {showTimeline ? 'LOOPS' : 'TIMELINE'}
          </button>
        </div>

        {/* Circle toggle cards */}
        <div className="debug-menu__circle-grid">
          {circles.map((circle) => {
            const enabled = !disabledCircles.has(circle.id);
            return (
              <button
                key={circle.id}
                className={`debug-menu__circle-btn ${enabled ? 'debug-menu__circle-btn--on' : 'debug-menu__circle-btn--off'}`}
                onClick={() => onToggleCircle(circle.id)}
                title={circle.sublabel}
              >
                <svg width="26" height="26" viewBox="0 0 26 26" className="shape-preview">
                  <circle cx="13" cy="13" r="10" fill="none" stroke="white" strokeWidth="1" />
                </svg>
                <span className="debug-menu__circle-btn-label">{circle.label}</span>
              </button>
            );
          })}
        </div>

        <div className="debug-menu__grid">
          {geometryList.map((geom) => {
            const key = `${geom.divisions}-${geom.skip}`;
            const enabled = !disabledGeos.has(key);
            const label = geom.skip === 1
              ? `{${geom.divisions}}`
              : `{${geom.divisions}/${geom.skip}}`;

            return (
              <button
                key={key}
                className={`debug-menu__btn ${enabled ? 'debug-menu__btn--on' : 'debug-menu__btn--off'}`}
                onClick={() => onToggle(key)}
                title={`${geom.divisions} divisions, skip ${geom.skip}${geom.isRegular ? '' : ' (star)'}`}
              >
                <ShapePreview divisions={geom.divisions} path={geom.path} />
                <span className="debug-menu__btn-label">{label}</span>
              </button>
            );
          })}
        </div>

        <div className="debug-menu__header">
          <button className="debug-menu__action" onClick={onEnableAll}>
            ALL ON
          </button>
          <button className="debug-menu__action" onClick={onDisableAll}>
            ALL OFF
          </button>
        </div>

        <div className="debug-menu__settings-row">
          <div className="debug-menu__setting">
            <span className="debug-menu__setting-label">HOLD NOTES FOR</span>
            <select
              className="debug-menu__select"
              value={noteLookback}
              onChange={(e) => onNoteLookbackChange(Number(e.target.value))}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span className="debug-menu__setting-label">ROTATIONS</span>
          </div>
        </div>

        <div className="debug-menu__settings-row">
          <button
            className={`debug-menu__action ${trailMode === 'geometry' ? 'debug-menu__action--active' : ''}`}
            onClick={onToggleTrailMode}
          >
            TRAIL: {trailMode === 'time' ? 'TIME' : 'GEO'}
          </button>
          <label className="debug-menu__live-toggle">
            <input
              type="checkbox"
              checked={showTraditionalTime}
              onChange={(e) => onToggleTraditionalTime(e.target.checked)}
            />
            <span className="debug-menu__live-label">TRADITIONAL TIME</span>
          </label>
        </div>

        {/* Journal data: import from the old exochronometer.vercel.app site or a backup file */}
        <div className="debug-menu__journal">
          <div className="debug-menu__journal-title">JOURNAL DATA</div>
          <div className="debug-menu__settings-row">
            <button
              className={`debug-menu__action ${showImport ? 'debug-menu__action--active' : ''}`}
              onClick={() => {
                setShowImport((s) => !s);
                setImportStatus(null);
              }}
            >
              IMPORT JOURNAL
            </button>
            <button className="debug-menu__action" onClick={onExportNotes}>
              EXPORT JOURNAL
            </button>
          </div>
          {showImport && (
            <div className="debug-menu__import-panel">
              <p className="debug-menu__import-help">
                Moving from exochronometer.vercel.app? Open that site, run the
                snippet below in the browser console (or paste it in the address
                bar after typing &quot;javascript:&quot;), then paste the copied
                text here — or choose an exported .json file.
              </p>
              <code className="debug-menu__import-snippet">
                {"copy(localStorage.getItem('exochronometer_notes'))"}
              </code>
              <textarea
                className="debug-menu__import-textarea"
                placeholder='Paste journal JSON here, e.g. [{"id":"...","timestamp":...,"text":"..."}]'
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                rows={4}
              />
              <div className="debug-menu__settings-row">
                <button
                  className="debug-menu__action"
                  disabled={!importText.trim()}
                  onClick={() => runImport(importText)}
                >
                  IMPORT PASTED
                </button>
                <button
                  className="debug-menu__action"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                >
                  IMPORT FILE
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json,text/plain"
                  style={{ display: 'none' }}
                  onChange={handleFileChosen}
                />
              </div>
            </div>
          )}
          {importStatus && (
            <div
              className={`debug-menu__import-status ${importStatus.ok ? 'debug-menu__import-status--ok' : 'debug-menu__import-status--err'}`}
            >
              {importStatus.message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DebugMenu;
